import React from 'react';
import {
  Formik,
  Field,
  FormikProps,
} from 'formik';
import {
  useStripe,
  useElements,
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
} from '@stripe/react-stripe-js';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { CSSObjectWithLabel } from 'react-select';
import {
  useSelector,
} from 'react-redux';

import {
  coreSelectors,
} from '@gymcloud/core';

import {
  Button,
  TextField,
  SelectField,
} from 'web-components';

import {
  Flex,
  Text,
} from 'web-styled';

import {
  data,
  zipRegex,
} from 'web-utils';

import * as S from './styled';

// Types
interface FormValues {
  name: string;
  cardNumberIsValid?: boolean | null;
  cardExpiryIsValid?: boolean | null;
  CVCIsValid?: boolean | null;
  country: string;
  state: string;
  zip?: string;
}

interface Props {
  submitText?: string;
  onCancel: () => void;
  onSuccess: (response: any, actions: FormikProps<FormValues>) => void;
}

interface LocationConfig {
  label: string;
  placeholder: string;
  zipPlaceholder: string;
  options: Array<{ value: string; label: string }>;
}

// Constants
const COUNTRIES_REQUIRING_ZIP = ['us', 'ca'];

const VALIDATION_SCHEMA = () => Yup.lazy(
  values => Yup.object().shape({
    name: Yup.string()
      .required('Cardholder name is required'),
    cardNumberIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid card number')
      .required('This field is required'),
    cardExpiryIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid card expiration date')
      .required('This field is required'),
    CVCIsValid: Yup.mixed()
      .oneOf([true, null], 'Please enter a valid CVC')
      .required('This field is required'),

    ...(COUNTRIES_REQUIRING_ZIP.indexOf(values.country) !== -1
      ? {
        state: Yup.string()
          .required('Required'),
        zip: Yup.string()
          .matches(
            zipRegex[values.country],
            {
              message: 'Please enter a valid zip code.',
              excludeEmptyString: true,
            },
          )
          .required('Required'),
      }
      : {}
    ),
  })
)

const LOCATION_CONFIG: Record<string, LocationConfig> = {
  ca: {
    label: 'Province*',
    placeholder: 'Select your province',
    zipPlaceholder: 'A1A 1A1',
    options: data.caStatesOptions,
  },
  us: {
    label: 'State*',
    placeholder: 'Select your state',
    zipPlaceholder: '12345',
    options: data.usStatesOptions,
  },
};

// Custom hooks
const useStripeSubmit = (onSuccess: Props['onSuccess']) => {
  const stripe = useStripe();
  const elements = useElements();

  return async (values: FormValues, actions: FormikProps<FormValues>) => {
    if (!stripe || !elements) {
      console.error("Stripe.js hasn't loaded yet.");
      return;
    }

    const cardElement = elements.getElement(CardNumberElement);
    if (!cardElement) {
      console.error("Card element not found.");
      return;
    }

    try {
      const result = await stripe.createPaymentMethod({
        type: 'card',
        card: cardElement,
        billing_details: {
          name: values.name,
          address: {
            country: values.country,
            state: values.state,
            postal_code: values.zip,
          },
        },
      })

      if (result.error) {
        console.error('Stripe error:', result.error.message)
        toast.error(result.error.message)
        // TODO: Handle error properly - set form field errors
        actions.setSubmitting(false)
      } else {
        onSuccess(result, actions)
      }
    } catch (error) {
      console.error('Payment method creation failed:', error)
      actions.setSubmitting(false)
    }
  };
};

const SecurityNotice: React.FC = () => (
  <Flex align="center" spacing={10}>
    <S.LockIcon />
    <Text lineHeight="1.1em">
      This is a secure 128-bit SSL
      <br />
      encrypted payment.
    </Text>
  </Flex>
);

const FormActions: React.FC<{
  submitText: string;
  isSubmitting: boolean;
  onCancel: () => void;
}> = ({ submitText, isSubmitting, onCancel }) => (
  <Flex spacing={10}>
    <Button
      ghost
      type="button"
      appearance="danger"
      onClick={onCancel}
      borderRadius="5px"
    >
      Cancel
    </Button>
    <Button
      type="submit"
      appearance="primary"
      borderRadius="5px"
      isLoading={isSubmitting}
      isDisabled={isSubmitting}
    >
      {submitText}
    </Button>
  </Flex>
);

const LocationFields: React.FC<{
  country: string;
  formikProps: FormikProps<FormValues>;
}> = ({ country, formikProps }) => {
  const locationConfig = LOCATION_CONFIG[country];

  if (!locationConfig || !COUNTRIES_REQUIRING_ZIP.includes(country)) {
    return null;
  }

  return (
    <S.Location>
      <Field
        name="state"
        label={locationConfig.label}
        color="#333333"
        spacing={5}
        labelWeight="bold"
        options={locationConfig.options}
        component={SelectField}
        maxMenuHeight={500}
        placeholder={locationConfig.placeholder}
        theme={{ borderRadius: 5 }}
        styles={{
          option: (provided: CSSObjectWithLabel) => ({
            ...provided,
            fontSize: '12px',
            lineHeight: '12px',
            fontWeight: 500,
          }),
          valueContainer: (provided: CSSObjectWithLabel) => ({
            ...provided,
            color: '#495057',
            fontSize: '1rem',
            lineHeight: '1.5',
            fontWeight: 500,
          }),
          control: (provided: CSSObjectWithLabel) => ({
            ...provided,
            height: '30px',
          }),
        }}
      />

      <Field
        type="text"
        name="zip"
        label="Zip*"
        color="#333333"
        spacing={5}
        height="38px"
        component={TextField}
        placeholder={locationConfig.zipPlaceholder}
        autoComplete="off"
        labelWeight="bold"
      />
    </S.Location>
  );
};

const ElementOptions = {
  style: {
    base: {
      '::placeholder': {
        color: '#c1c1c1', // placeholder color
      },
    },
  }
}

const mapState = state => ({
  profile: coreSelectors.getProfile('user_profile')(state),
});

// Main component
const AddCardForm: React.FC<Props> = ({
  submitText = 'Add',
  onCancel,
  onSuccess,
}) => {
  const handleSubmit = useStripeSubmit(onSuccess);
  const { profile } = useSelector(mapState);

  const handleFormSubmit = (
    values: FormValues,
    actions: FormikProps<FormValues>
  ) => {
    // Ensure validation fields are set
    const validationFields: (keyof FormValues)[] = [
      'cardNumberIsValid',
      'cardExpiryIsValid',
      'CVCIsValid'
    ];

    const updatedValues = { ...values };
    validationFields.forEach((field) => {
      if (!(field in values) || values[field] === undefined) {
        updatedValues[field] = null;
      }
    });

    handleSubmit(updatedValues, actions);
  }

  const handleCountryChange = (formikProps: FormikProps<FormValues>) => {
    formikProps.setFieldValue('zip', '');
    formikProps.setFieldValue('state', '');
  }


  const initialValues: FormValues = {
    name: '',
    cardNumberIsValid: null,
    cardExpiryIsValid: null,
    CVCIsValid: null,
    country: profile?.country?.toLowerCase() || 'us',
    state: profile?.state?.toLowerCase() || '',
    zip: profile?.zip || '',
  }

  return (
    <Flex width="100%">
      <Formik
        initialValues={initialValues}
        validationSchema={VALIDATION_SCHEMA}
        onSubmit={handleFormSubmit}
      >
        {(formikProps) => {
          const { values, errors, handleSubmit, isSubmitting, setFieldValue } = formikProps;

          return (
            <S.AddCardForm onSubmit={handleSubmit}>
              <Flex column spacing={15} width="100%">
                <S.CardNumContainer
                  hasError={
                    !!errors.cardNumberIsValid &&
                    'cardNumberIsValid' in values
                  }
                >
                  <Text
                    size="13px"
                    lineHeight="18px"
                    weight="bold"
                  >
                    Card Number *
                  </Text>
                  <CardNumberElement
                    options={ElementOptions}
                    className="card-element"
                    onChange={async (e) => {
                      await setFieldValue('cardNumberIsValid', e.complete);
                    }}
                  />
                  <Text className="error-message">
                    {errors.cardNumberIsValid}
                  </Text>
                </S.CardNumContainer>

                <Flex width="100%">
                  <S.ExpirationDate
                    hasError={
                      !!errors.cardExpiryIsValid &&
                      typeof values.cardExpiryIsValid !== 'undefined'
                    }
                  >
                    <Text
                      size="13px"
                      lineHeight="18px"
                      weight="bold"
                    >
                      Expiration Date *
                    </Text>
                    <CardExpiryElement
                      options={ElementOptions}
                      className="card-element"
                      onChange={(e) => setFieldValue('cardExpiryIsValid', e.complete)}
                    />
                    <Text className="error-message">
                      {errors.cardExpiryIsValid}
                    </Text>
                  </S.ExpirationDate>

                  <S.CVVContainer
                    hasError={
                      !!errors.CVCIsValid &&
                      typeof values.CVCIsValid !== 'undefined'
                    }
                  >
                    <Text
                      size="13px"
                      lineHeight="18px"
                      weight="bold"
                    >
                      CVC *
                    </Text>
                    <CardCvcElement
                      options={ElementOptions}
                      className="card-element"
                      onChange={(e) => setFieldValue('CVCIsValid', e.complete)}
                    />
                    <Text className="error-message">
                      {errors.CVCIsValid}
                    </Text>
                  </S.CVVContainer>
                </Flex>
                {/* Cardholder Name */}
                <Field
                  name="name"
                  color="#333"
                  label="Card Holder Name *"
                  spacing={5}
                  height="38px"
                  component={TextField}
                  placeholder="Enter Name"
                  autoComplete="off"
                  labelWeight="bold"
                />

                {/* Country Selection */}
                <Field
                  name="country"
                  label="Country*"
                  color="#121827"
                  labelWeight="bold"
                  spacing={5}
                  options={data.countriesOptions}
                  component={SelectField}
                  onChange={() => handleCountryChange(formikProps)}
                  maxMenuHeight={500}
                  placeholder="-- Select Country --"
                  theme={{ borderRadius: 5 }}
                  styles={{
                    option: (provided: CSSObjectWithLabel) => ({
                      ...provided,
                      fontSize: '12px',
                      lineHeight: '12px',
                      fontWeight: 500,
                    }),
                    valueContainer: (provided: CSSObjectWithLabel) => ({
                      ...provided,
                      color: '#495057',
                      fontSize: '1rem',
                      lineHeight: '1.5',
                      fontWeight: 500,
                    }),
                    control: (provided: CSSObjectWithLabel) => ({
                      ...provided,
                      height: '30px',
                    }),
                  }}
                />

                {/* Location Fields (State/Province and Zip) */}
                <LocationFields country={values.country} formikProps={formikProps} />
              </Flex>

              {/* Footer with Security Notice and Actions */}
              <Flex width="100%" align="center" justify="space-between">
                <SecurityNotice />
                <FormActions
                  submitText={submitText}
                  isSubmitting={isSubmitting}
                  onCancel={onCancel}
                />
              </Flex>
            </S.AddCardForm>
          );
        }}
      </Formik>
    </Flex>
  );
};

export default AddCardForm;
